/**
 * Action Button - per `Design/Mockups/_Notes - Share Modal.md`.
 *
 * Full-width, 44px tap target, 8px radius. Primary variant: `primary` fill +
 * `primary-foreground` text. Secondary variant: `muted` fill + `foreground`
 * text. Optional Lucide icon to the left of the label.
 */

import { iconSvg, type LucideName } from './icons';

export type ActionButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive';

export type ActionButtonSize = 'sm' | 'md' | 'lg';

export interface ActionButtonProps {
  variant: ActionButtonVariant;
  label: string;
  icon?: LucideName;
  iconPosition?: 'left' | 'right';
  onClick: () => void;
  disabled?: boolean;
  size?: ActionButtonSize;
}

export interface ActionButtonHandle {
  el: HTMLButtonElement;
  setLabel(l: string): void;
  setDisabled(d: boolean): void;
  update(props: ActionButtonProps): void;
  destroy(): void;
}

export type ButtonHandle = ActionButtonHandle;

const VARIANT_CLASS: Record<ActionButtonVariant, string> = {
  primary: 'gv-action-button-primary',
  secondary: 'gv-action-button-secondary',
  tertiary: 'gv-action-button-tertiary',
  destructive: 'gv-action-button-destructive',
};

const SIZE_CLASS: Record<ActionButtonSize, string> = {
  sm: 'gv-action-button-sm',
  md: 'gv-action-button-md',
  lg: 'gv-action-button-lg',
};

export function mountActionButton(target: HTMLElement, props: ActionButtonProps): ActionButtonHandle {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gv-action-button';

  let current = props;
  let labelEl: HTMLSpanElement;

  const handler = (): void => {
    if (current.disabled === true) return;
    current.onClick();
  };
  btn.addEventListener('click', handler);

  function render(p: ActionButtonProps): void {
    current = p;
    btn.className = 'gv-action-button';
    btn.classList.add(VARIANT_CLASS[p.variant]);
    btn.classList.add(SIZE_CLASS[p.size ?? 'md']);

    btn.replaceChildren();
    const iconPos = p.iconPosition ?? 'left';
    if (p.icon !== undefined && iconPos === 'left') {
      btn.appendChild(iconSvg(p.icon, 18));
    }
    labelEl = document.createElement('span');
    labelEl.className = 'gv-action-button-label';
    labelEl.textContent = p.label;
    btn.appendChild(labelEl);
    if (p.icon !== undefined && iconPos === 'right') {
      btn.appendChild(iconSvg(p.icon, 18));
    }

    if (p.disabled === true) {
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    }
  }

  render(props);
  target.appendChild(btn);

  return {
    el: btn,
    setLabel(l: string): void {
      current = { ...current, label: l };
      labelEl.textContent = l;
    },
    setDisabled(d: boolean): void {
      current = { ...current, disabled: d };
      if (d) {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      }
    },
    update(next: ActionButtonProps): void {
      render(next);
    },
    destroy(): void {
      btn.removeEventListener('click', handler);
      btn.remove();
    },
  };
}
